import { useState } from "react";
import { useClinicDoctors, type ClinicDoctor } from "@/lib/queries";
import { useCreateClinicDoctor, useUpdateClinicDoctor, useDeleteClinicDoctor } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash } from "lucide-react";
import { fmtINR } from "@emr/shared/india";

function ClinicDoctorFormDialog({ open, onOpenChange, doctor }: { open: boolean; onOpenChange: (v: boolean) => void; doctor?: ClinicDoctor | null }) {
  const [form, setForm] = useState({
    name: doctor?.name ?? "",
    qualifications: doctor?.qualifications ?? "",
    regNumber: doctor?.regNumber ?? "",
    specialty: doctor?.specialty ?? "",
    consultationFeePaise: doctor?.consultationFeePaise ?? 0,
  });
  const create = useCreateClinicDoctor();
  const update = useUpdateClinicDoctor();

  const submit = async () => {
    const body = {
      name: form.name.trim(),
      qualifications: form.qualifications.trim() || undefined,
      regNumber: form.regNumber.trim() || undefined,
      specialty: form.specialty.trim() || undefined,
      consultationFeePaise: form.consultationFeePaise || undefined,
    };
    try {
      if (doctor) await update.mutateAsync({ id: doctor.id, body });
      else await create.mutateAsync(body);
      onOpenChange(false);
    } catch { /* toast handled in hook */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{doctor ? "Edit doctor" : "Add clinic doctor"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Qualifications</Label><Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} placeholder="e.g. MBBS, MD" /></div>
          <div><Label>Reg Number</Label><Input value={form.regNumber} onChange={(e) => setForm({ ...form, regNumber: e.target.value })} /></div>
          <div><Label>Specialty</Label><Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
          <div>
            <Label>Consultation Fee (₹)</Label>
            <Input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              pattern="[0-9]*"
              value={form.consultationFeePaise ? String(Math.round(form.consultationFeePaise / 100)) : ""}
              onChange={(e) => setForm({ ...form, consultationFeePaise: Math.round((parseFloat(e.target.value) || 0) * 100) })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" disabled={!form.name.trim() || create.isPending || update.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ClinicDoctorsSection({ canEdit }: { canEdit: boolean }) {
  const { data, isLoading } = useClinicDoctors();
  const [addOpen, setAddOpen] = useState(false);
  const [editDoctor, setEditDoctor] = useState<ClinicDoctor | null>(null);
  // Session counter bumps on every open so the dialog remounts fresh each time
  // and useState re-initializes from the current doctor prop (see SCHEMA.md §18 2026-07-20).
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditDoctor(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (d: ClinicDoctor) => { setEditDoctor(d); setSessionId((n) => n + 1); setAddOpen(true); };
  const del = useDeleteClinicDoctor();
  const rows = Array.isArray(data) ? data : (data as any)?.rows ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500">Doctors</CardTitle>
        {canEdit && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}><Plus size={14} className="mr-1" /> Add Doctor</Button>}
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
              <TableHead>Name</TableHead><TableHead>Qualifications</TableHead><TableHead>Reg #</TableHead><TableHead>Specialty</TableHead><TableHead>Fee</TableHead><TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={6}><Skeleton className="h-10" /></TableCell></TableRow> :
             rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted py-8">No doctors</TableCell></TableRow> :
             rows.map((d: ClinicDoctor) => (
              <TableRow key={d.id} className="even:bg-cream-50/50">
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell>{d.qualifications ?? "—"}</TableCell>
                <TableCell>{d.regNumber ?? "—"}</TableCell>
                <TableCell>{d.specialty ?? "—"}</TableCell>
                <TableCell>{d.consultationFeePaise ? fmtINR(d.consultationFeePaise) : "—"}</TableCell>
                <TableCell>
                  {canEdit && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(d)}><Pencil size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del.mutate(d.id)}><Trash size={14} /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <ClinicDoctorFormDialog key={sessionId} open={addOpen} onOpenChange={setAddOpen} doctor={editDoctor} />
    </Card>
  );
}
